import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from 'shared/components/Modal/Modal';
import { Text } from 'shared/components/Text/Text';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import { useBreakpoints } from 'hooks/useWindowSize';
import {
  AutoPaySuccessModalContainer,
  ContentItem,
  ContentList,
  GraphicContainerModal,
} from './OnboardingAutoPay.styles';

const AutopaySuccessIcon = React.lazy(() => import('shared/components/icons/AutopaySuccessIcon'));
const CheckmarkGraphic = React.lazy(() => import('shared/components/icons/CheckmarkGraphic'));

export type Props = {
  open: boolean;
  styleVariant?: 'default' | 'secondary';
  onContinue: () => void;
};

export function AutoPayModalSuccess({ open, styleVariant = 'default', onContinue }: Props) {
  const [isOpen, setIsOpen] = useState(open);

  const { t } = useTranslation();
  const breakpoints = useBreakpoints();

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  let graphic = <CheckmarkGraphic />;
  let title = t('onboarding.autoPay.enrolled.title');
  let bodyKey = 'onboarding.autoPay.enrolled.body';
  let buttonLabel = t('cta.continue');

  if (styleVariant === 'secondary') {
    graphic = <AutopaySuccessIcon />;
    title = t('onboarding.autoPay.enrolled.titleSecondary');
    bodyKey = 'onboarding.autoPay.enrolled.bodySecondary';
    buttonLabel = t('cta.gotIt');
  }

  const bodyList: string[] = t(bodyKey, {
    returnObjects: true,
  }) as string[];

  return (
    <Modal
      contentLabel={title}
      styleVariant="tertiary"
      isOpen={isOpen}
      isFullHeight={breakpoints === 'sm'}
      onRequestClose={handleClose}
      ctaButtons={{
        primary: {
          label: buttonLabel,
          onClick: onContinue,
        },
      }}
    >
      <AutoPaySuccessModalContainer>
        <GraphicContainerModal $enrolled $isSecondary={styleVariant === 'secondary'}>
          <Suspense fallback={<LoadingSpinner />}>{graphic}</Suspense>
        </GraphicContainerModal>

        <Text as="h2" $margin="20px 0" $fontWeight={500}>
          {title}
        </Text>

        <ContentList>
          {Array.isArray(bodyList) &&
            bodyList.map((item) => <ContentItem key={item}>{item}</ContentItem>)}
        </ContentList>
      </AutoPaySuccessModalContainer>
    </Modal>
  );
}

export default AutoPayModalSuccess;
